import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';

const CHART_COLORS = ['#4A9AF5', '#7B61FF', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

function DonutChart({ segments, total, size = 120 }) {
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const sum = segments.reduce((s, x) => s + x.count, 0) || 1;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E8EDF2" strokeWidth="14" />
        {segments.map((seg, i) => {
          const len = (seg.count / sum) * circ;
          const dash = `${len} ${circ - len}`;
          const el = (
            <circle
              key={seg.label}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth="14"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-2xl font-bold text-sap-text">{total}</span>
      </div>
    </div>
  );
}

function LineChart({ months, series, height = 220 }) {
  const width = 520;
  const pad = { top: 16, right: 16, bottom: 32, left: 40 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const yMin = 40;
  const yMax = 100;

  const xStep = months.length > 1 ? innerW / (months.length - 1) : innerW;

  const toY = (v) => pad.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  const toX = (i) => pad.left + i * xStep;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      {[40, 60, 80, 100].map((tick) => (
        <g key={tick}>
          <line
            x1={pad.left}
            y1={toY(tick)}
            x2={width - pad.right}
            y2={toY(tick)}
            stroke="#E8EDF2"
            strokeWidth="1"
          />
          <text x={pad.left - 8} y={toY(tick) + 4} textAnchor="end" className="fill-sap-textLight text-[10px]">
            {tick}%
          </text>
        </g>
      ))}
      {months.map((m, i) => (
        <text key={m} x={toX(i)} y={height - 8} textAnchor="middle" className="fill-sap-textLight text-[10px]">
          {m}
        </text>
      ))}
      {series.map((s, si) => {
        const pts = (s.points || []).map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
        return (
          <polyline
            key={s.vendor_id}
            fill="none"
            stroke={CHART_COLORS[si % CHART_COLORS.length]}
            strokeWidth="2"
            points={pts}
          />
        );
      })}
    </svg>
  );
}

function BarChart({ items, height = 220 }) {
  const max = Math.max(...items.map((x) => x.satisfaction_pct), 1);
  return (
    <div className="flex items-end gap-2 h-[220px] px-2 pb-6">
      {items.map((item, i) => {
        const pct = (item.satisfaction_pct / max) * 100;
        return (
          <div key={item.vendor_id} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full group">
            <span className="text-[9px] text-sap-textLight mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {item.satisfaction_pct}%
            </span>
            <div
              className="w-full max-w-[36px] rounded-t-md transition-all"
              style={{
                height: `${Math.max(8, pct)}%`,
                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
              }}
              title={`${item.vendor_name}: ${item.satisfaction_pct}%`}
            />
            <span className="text-[8px] text-sap-textLight mt-2 truncate w-full text-center" title={item.vendor_name}>
              {item.vendor_name.split(' ')[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MetricCard({ title, value, subtitle }) {
  return (
    <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4 flex flex-col justify-center min-h-[140px]">
      <h3 className="text-xs font-semibold text-sap-text mb-3">{title}</h3>
      <div className="text-4xl font-light text-sap-text">{value}</div>
      {subtitle && <p className="text-[11px] text-sap-textLight mt-2">{subtitle}</p>}
    </div>
  );
}

function tierBadge(tier) {
  const styles = {
    Gold: 'bg-amber-100 text-amber-800 border-amber-200',
    Silver: 'bg-slate-100 text-slate-700 border-slate-200',
    Bronze: 'bg-orange-100 text-orange-800 border-orange-200',
    Standard: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[tier] || styles.Standard}`}>
      {tier}
    </span>
  );
}

export default function VendorManagement() {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('performance');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const initialLoadDone = useRef(false);

  const fetchWorkspace = useCallback(async (pageNum, limitNum, tableOnly) => {
    if (tableOnly) setTableLoading(true);
    else setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ page: String(pageNum), limit: String(limitNum) });
      const res = await api.get(`/vendors/workspace?${qs}`);
      setData((prev) => (tableOnly && prev ? { ...prev, ...res } : res));
    } catch (e) {
      setError(e.message || 'Failed to load vendor workspace');
      if (!tableOnly) setData(null);
    } finally {
      if (tableOnly) setTableLoading(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch({ type: 'SET_BREADCRUMB', payload: ['Home', 'Vendor Management Workspace'] });
  }, [dispatch]);

  useEffect(() => {
    const tableOnly = initialLoadDone.current;
    initialLoadDone.current = true;
    fetchWorkspace(page, limit, tableOnly);
  }, [page, limit, fetchWorkspace]);

  const refreshAll = () => {
    fetchWorkspace(page, limit, false);
  };

  const summary = data?.summary || {};
  const kpiGroups = data?.vendors_by_kpi_group || [];
  const trend = data?.performance_trend || { months: [], series: [] };
  const satisfaction = data?.vendor_satisfaction || [];
  const vendors = data?.vendors || [];
  const vendorsTotal = data?.vendors_total ?? summary.total_vendors ?? 0;
  const totalPages = Math.max(1, Math.ceil(vendorsTotal / limit));
  const from = vendorsTotal === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, vendorsTotal);

  return (
    <div className="space-y-4 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-sap-text">Vendor Management Workspace</h1>
          <p className="text-xs text-sap-textLight mt-0.5">Vendor scores and tiers from ML risk analysis; operational charts from PO/rework data</p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-sap-border bg-white text-sap-text hover:bg-sap-bg disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-sap-border/60">
        <button
          type="button"
          onClick={() => setActiveTab('performance')}
          className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
            activeTab === 'performance'
              ? 'border-sap-blue text-sap-blue'
              : 'border-transparent text-sap-textLight hover:text-sap-text'
          }`}
        >
          Performance
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-sm text-sap-textLight">Loading vendor workspace…</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4 min-h-[140px]">
              <h3 className="text-xs font-semibold text-sap-text mb-3">Vendors by KPI group</h3>
              <div className="flex items-center gap-4">
                <DonutChart segments={kpiGroups} total={summary.total_vendors || 0} />
                <ul className="flex-1 space-y-1.5 min-w-0">
                  {kpiGroups.slice(0, 5).map((g, i) => (
                    <li key={g.label} className="flex items-center gap-2 text-[11px]">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <span className="truncate text-sap-text flex-1">{g.label}</span>
                      <span className="font-semibold text-sap-text">{g.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <MetricCard
              title="New contracts"
              value={summary.new_contracts ?? 0}
              subtitle="Purchase orders created in the last 30 days"
            />
            <MetricCard
              title="Contracts ending"
              value={summary.contracts_ending ?? 0}
              subtitle="Open POs ending in the next 90 days"
            />
            <MetricCard
              title="Improvement initiatives"
              value={summary.improvement_initiatives ?? 0}
              subtitle="Active rework / quality initiatives"
            />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4">
              <h3 className="text-xs font-semibold text-sap-text mb-1">
                Performance score by vendor in the last 90 days
              </h3>
              <LineChart months={trend.months} series={trend.series} />
              {trend.series?.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 px-1">
                  {trend.series.map((s, i) => (
                    <div key={s.vendor_id} className="flex items-center gap-1.5 text-[10px] text-sap-textLight">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <span className="truncate max-w-[120px]">{s.vendor_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4">
              <h3 className="text-xs font-semibold text-sap-text mb-1">Vendor satisfaction by vendor</h3>
              {satisfaction.length === 0 ? (
                <p className="text-xs text-sap-textLight py-16 text-center">No satisfaction data yet</p>
              ) : (
                <>
                  <BarChart items={satisfaction} />
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 border-t border-sap-border/40 pt-2">
                    {satisfaction.map((v, i) => (
                      <span key={v.vendor_id} className="text-[9px] text-sap-textLight">
                        <span className="font-medium text-sap-text">{v.vendor_name}:</span> {v.satisfaction_pct.toFixed(0)}%
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Vendors table */}
          <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-sap-border/60 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-sap-text">My vendors</h3>
                <p className="text-[10px] text-sap-textLight mt-0.5">
                  {vendorsTotal} active vendor{vendorsTotal !== 1 ? 's' : ''} in database
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/purchasing/vendors')}
                className="text-xs text-sap-blue hover:underline font-medium"
              >
                Open vendor list →
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F7F5F2] text-left text-[11px] uppercase tracking-wide text-sap-textLight">
                    <th className="px-4 py-2.5 font-semibold">Name</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Vendor score</th>
                    <th className="px-4 py-2.5 font-semibold">Vendor type</th>
                    <th className="px-4 py-2.5 font-semibold">Rank tier</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sap-textLight text-xs">Loading…</td>
                    </tr>
                  ) : vendors.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sap-textLight text-xs">
                        No vendors found. Add vendors under Business Partners → Vendors.
                      </td>
                    </tr>
                  ) : (
                    vendors.map((v) => (
                      <tr
                        key={v.vendor_id}
                        className="border-t border-sap-border/40 hover:bg-sap-blue/5 cursor-pointer"
                        onClick={() => navigate(`/vendor-management/vendor/${v.vendor_id}`)}
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-sap-blue">{v.vendor_name}</div>
                          <div className="text-[10px] text-sap-textLight">{v.vendor_code}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="font-semibold text-sap-text">{Number(v.vendor_score || 0).toFixed(1)}</span>
                          {v.risk_score != null && (
                            <div className="text-[10px] text-sap-textLight">Risk {Number(v.risk_score).toFixed(1)}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-sap-text">{v.vendor_type}</td>
                        <td className="px-4 py-2.5">{tierBadge(v.rank_tier)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t border-sap-border/60 flex flex-wrap items-center justify-between gap-3 text-xs text-sap-textLight">
              <span>Showing {from}-{to} of {vendorsTotal}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || tableLoading}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ‹
                </button>
                <span>{page} / {totalPages}</span>
                <button
                  type="button"
                  disabled={page >= totalPages || tableLoading}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ›
                </button>
              </div>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="border rounded px-2 py-1 bg-white text-sap-text"
                >
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
